import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { MorphemeMnemonicsPanelContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content._context";
import { useDrillFooterContainerContentHeight } from "@-/dev.improve.haniverse.routes.nav.app._components.drill.footer";
import {
  useCardScrollOptIn,
  useIsReviewSwipeScrollLocked,
} from "@-/dev.improve.haniverse.lib.cards.review-swipe";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { getGraphemeMeaningImageUrlPath } from "@-/dev.improve.haniverse.assets.graphemes.meaning.images/public";
import type { MorphemeMnemonicsMeaningViewImageMode } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._components.hero-section";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from "@-/com.npmjs.react-native";
import type { ScrollView } from "@-/com.npmjs.react-native";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import { KeyboardController, KeyboardEvents } from "@-/com.npmjs.react-native-keyboard-controller";

const CONTENT_PADDING_TOP = 4;
// Below this the illustration is too small to read, so a collapsed card hides it behind Show More
// instead of squeezing it into the leftover space.
const MIN_FIT_IMAGE_HEIGHT = 96;

export function useMorphemeMnemonicsMeaningViewTabScroll({
  extraBottomPadding = 0,
  showEditButton = true,
}: {
  extraBottomPadding?: number;
  showEditButton?: boolean;
}) {
  const [isMnemonicEditing, setIsMnemonicEditing] = useContextSelectors(
    MorphemeMnemonicsPanelContext,
    (c) => c.isEditing,
    (c) => c.setIsEditing,
  );
  const [isNotesEditing, setIsNotesEditing] = useRouteBodyState("notes-editing", false);
  const isEditing = isMnemonicEditing || isNotesEditing;
  const handleEditPress = useCallback(() => {
    setIsNotesEditing(!isEditing);
    setIsMnemonicEditing(!isEditing);
  }, [isEditing, setIsMnemonicEditing, setIsNotesEditing]);
  const handleStartNotesEditing = useCallback(() => {
    setIsNotesEditing(true);
  }, [setIsNotesEditing]);
  const handleFinishNotesEditing = useCallback(() => {
    setIsNotesEditing(false);
  }, [setIsNotesEditing]);
  const [grapheme] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
  );
  const { enableScroll, isCard } = useCardScrollOptIn();
  // A card never renders content that overflows it: the illustration fills whatever space is left
  // below the top sheet, or stays collapsed behind an in-flow Show More when too little is. Expanding
  // (Show More, pressing the image, or editing) renders everything and makes the card scroll.
  const [isExpanded, setIsExpanded] = useRouteBodyState("meaning-expanded", false);
  const isCollapsible = isCard && !isEditing && !isExpanded;
  useEffect(() => {
    if (isEditing || (isCard && isExpanded)) {
      enableScroll();
    }
  }, [enableScroll, isCard, isEditing, isExpanded]);
  // Mirrors `useMorphemeMnemonicsMeaningViewImageSectionGate`, so a hidden image never reserves
  // collapsed-card space.
  const [isGraphemeImagesEnabled] = useDeviceSetting("isGraphemeImagesEnabled");
  const meaningImageUrlPath = isGraphemeImagesEnabled
    ? getGraphemeMeaningImageUrlPath(grapheme)
    : null;
  const [loadedImageUrlPath, setLoadedImageUrlPath] = useState<string | null>(null);
  const isImageLoaded = meaningImageUrlPath !== null && loadedImageUrlPath === meaningImageUrlPath;
  const handleImageLoad = useCallback(() => {
    setLoadedImageUrlPath(meaningImageUrlPath);
  }, [meaningImageUrlPath]);
  const handleImageError = useCallback(() => {
    setLoadedImageUrlPath(null);
  }, []);
  const scrollViewRef = useRef<ScrollView>(null);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useRouteBodyState("meaning-scroll-y", 0);
  const [initialContentOffset] = useState(() => ({ x: 0, y: scrollY }));
  const [imageLayout, setImageLayout] = useState({ top: 0, width: 0 });
  const [windowHeight] = useContextSelectors(RootContext, (c) => c.windowDimensions.height);
  // A rating swipe owns the gesture while it is in flight, so the card must not also scroll under
  // it. Outside a review there is no provider and this is always false.
  const isScrollLocked = useIsReviewSwipeScrollLocked();

  // Pad the scroll content so its bottom can clear whatever overlaps the bottom of the scroll
  // viewport — the keyboard, plus the drill rating footer that rides on top of it (via
  // `KeyboardStickyView`) in the card-reveal flow. `KeyboardAvoidingView` doesn't react when the
  // keyboard is already open at mount (reveal preserves focus across navigation, so no show event
  // fires here), so we track the keyboard height ourselves. We pad only by how much that occluding
  // stack actually *overlaps* the viewport — not its full height — so scrolling to the end rests
  // the last line just above the occlusion instead of leaving a tall blank gap below it.
  const [keyboardHeight, setKeyboardHeight] = useState(() =>
    KeyboardController.isVisible() ? KeyboardController.state().height : 0,
  );
  const footerHeight = useDrillFooterContainerContentHeight();
  const [scrollViewBottomY, setScrollViewBottomY] = useState(0);
  useEffect(() => {
    const showSub = KeyboardEvents.addListener("keyboardDidShow", (event) => {
      setKeyboardHeight(event.height);
    });
    const hideSub = KeyboardEvents.addListener("keyboardDidHide", () => {
      setKeyboardHeight(0);
    });
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const occlusionHeight = keyboardHeight + footerHeight;
  const bottomOcclusionOverlap =
    occlusionHeight > 0 && scrollViewBottomY > 0
      ? Math.max(0, scrollViewBottomY - (windowHeight - occlusionHeight))
      : 0;

  const contentContainerStyle = useMemo(
    () => ({
      paddingBottom: 4 + extraBottomPadding + bottomOcclusionOverlap,
      // The page surfaces sit 9px from the device edges, like the toolbar controls above them.
      paddingHorizontal: 9,
      paddingTop: CONTENT_PADDING_TOP,
    }),
    [extraBottomPadding, bottomOcclusionOverlap],
  );

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setViewportHeight(event.nativeEvent.layout.height);
    // Capture the scroll viewport's bottom edge in window coordinates so we can pad only by the
    // keyboard's overlap with it (the footer/rating bar can sit between them).
    scrollViewRef.current?.getNativeScrollRef()?.measureInWindow((_x, y, _width, height) => {
      setScrollViewBottomY(y + height);
    });
  }, []);

  const handleContentSizeChange = useCallback((_width: number, height: number) => {
    setContentHeight(height);
  }, []);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      setScrollY(event.nativeEvent.contentOffset.y);
    },
    [setScrollY],
  );

  const handleImageLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, y } = event.nativeEvent.layout;
    setImageLayout((current) => {
      const top = CONTENT_PADDING_TOP + y;
      return current.top === top && current.width === width ? current : { top, width };
    });
  }, []);

  // Collapsed layout. The mnemonic and decomposition live in the top sheet, so the page below it
  // holds only the illustration: it fills the space left, or hides behind Show More when that space
  // is too small to read it.
  const availableHeight =
    viewportHeight - contentContainerStyle.paddingTop - contentContainerStyle.paddingBottom;
  const isMeasured = viewportHeight > 0;
  const isImageCollapsed =
    isCollapsible && isMeasured && isImageLoaded && availableHeight < MIN_FIT_IMAGE_HEIGHT;
  const isImageFit = isCollapsible && isMeasured && !isImageCollapsed;
  const imageMode: MorphemeMnemonicsMeaningViewImageMode = !isCollapsible
    ? "scroll"
    : isImageFit
      ? "fit"
      : "hidden";

  // In the fit layout the image fills exactly the space left, so the card ends flush with its
  // viewport instead of overflowing it.
  const imageHeight = isImageFit
    ? Math.min(
        imageLayout.width > 0 ? imageLayout.width : Number.POSITIVE_INFINITY,
        Math.max(0, Math.floor(availableHeight)),
      )
    : Math.min(
        imageLayout.width,
        // Keep a readable thumbnail even when it extends below the viewport. Scrolling first
        // reveals that minimum square, then grows it as more space becomes available.
        Math.max(
          imageLayout.width / 3,
          viewportHeight - imageLayout.top + scrollY - contentContainerStyle.paddingBottom,
        ),
      );
  const isImageShrunk = imageLayout.width > 0 && imageHeight < imageLayout.width - 1;

  const scrollToFullImage = useCallback(() => {
    // Invert the existing image-height equation: the smallest offset giving the square
    // illustration its full width. Clamp to the measured native scroll range.
    const target = Math.max(
      0,
      imageLayout.top + imageLayout.width + contentContainerStyle.paddingBottom - viewportHeight,
    );
    scrollViewRef.current?.scrollTo({
      animated: true,
      y: Math.min(Math.max(0, contentHeight - viewportHeight), Math.max(scrollY, target)),
    });
  }, [imageLayout, contentContainerStyle.paddingBottom, viewportHeight, contentHeight, scrollY]);

  // Expanding a fit image grows its spacer to the full square; scroll only once the ScrollView
  // reports the taller content, or the target would be clamped to the collapsed range.
  const pendingImageScrollFromContentHeightRef = useRef<number | null>(null);
  useEffect(() => {
    const fromContentHeight = pendingImageScrollFromContentHeightRef.current;
    if (fromContentHeight !== null && contentHeight !== fromContentHeight) {
      pendingImageScrollFromContentHeightRef.current = null;
      scrollToFullImage();
    }
  }, [contentHeight, scrollToFullImage]);

  const handleImagePress = useCallback(() => {
    // Like Show More, a tap on a collapsed card's image expands the card and makes it scroll; a
    // shrunk image then scrolls to its full square once the expanded content has grown.
    if (isCollapsible) {
      if (isImageShrunk) {
        pendingImageScrollFromContentHeightRef.current = contentHeight;
      }
      setIsExpanded(true);
      enableScroll();
      return;
    }
    enableScroll();
    scrollToFullImage();
  }, [contentHeight, enableScroll, isCollapsible, isImageShrunk, scrollToFullImage, setIsExpanded]);

  const handleShowMore = useCallback(() => {
    setIsExpanded(true);
    enableScroll();
  }, [enableScroll, setIsExpanded]);

  return {
    contentContainerStyle,
    handleContentSizeChange,
    handleEditPress,
    handleLayout,
    handleScroll,
    initialContentOffset,
    isEditing,
    scrollEnabled: !isScrollLocked,
    scrollViewRef,
    sectionsLayout: {
      image: {
        height: imageHeight,
        isLoaded: isImageLoaded,
        mode: imageMode,
        onError: handleImageError,
        onLayout: handleImageLayout,
        onLoad: handleImageLoad,
        onPress: handleImagePress,
        showsExpandHint: isImageShrunk,
        width: imageLayout.width,
      },
      notesEditing: {
        isEditing: isNotesEditing,
        onFinish: handleFinishNotesEditing,
        onStart: handleStartNotesEditing,
      },
      onShowMore: handleShowMore,
      showsShowMore: isImageCollapsed,
    },
    showEditButton,
  };
}
